function Privacy() {
  return (
    <div className="content-page">
      <h1>Privacy Policy</h1>
      <p className="subtitle">Last updated: 7 April 2026</p>

      <h2>Summary</h2>
      <p>
        Doccy does not collect, store, or transmit any personal data. Your
        documents stay on your device, encrypted, and under your control.
      </p>

      <h2>What data Doccy stores</h2>
      <p>
        Doccy stores the passport details, visa information, travel
        authorisations, trusted traveller memberships, and other identity
        documents that you manually enter or scan using the camera. This data is:
      </p>
      <ul>
        <li>Stored locally on your device</li>
        <li>Encrypted with AES-256-GCM before being written to disk</li>
        <li>Protected by Face ID or Touch ID</li>
      </ul>
      <p>
        If you enable iCloud Sync (a Pro feature), your encrypted data is stored
        in your personal iCloud account via Apple's CloudKit. Doccy encrypts all
        data before it leaves your device — iCloud stores only encrypted blobs
        that cannot be read by anyone, including Apple or the developers of Doccy.
      </p>

      <h2>What data Doccy does NOT collect</h2>
      <ul>
        <li>No personal information (name, email, phone number)</li>
        <li>No account creation or sign-in required</li>
        <li>No analytics or usage tracking</li>
        <li>No advertising identifiers</li>
        <li>No crash reporting data sent to third parties</li>
        <li>No location data</li>
        <li>No cookies or web tracking</li>
      </ul>

      <h2>Camera usage</h2>
      <p>
        If you use the passport scanning feature, Doccy accesses your device
        camera to read the Machine Readable Zone (MRZ) on your passport. All
        image processing happens entirely on your device using Apple's Vision
        framework. No images or scanned text are stored, transmitted, or sent
        to any server.
      </p>

      <h2>Biometric data</h2>
      <p>
        Doccy uses Face ID or Touch ID to secure access to your vault. Biometric
        authentication is handled entirely by iOS through the LocalAuthentication
        framework. Doccy never accesses, stores, or processes your biometric
        data — it only receives a yes/no response from the operating system.
      </p>

      <h2>Encryption</h2>
      <p>
        All stored data is encrypted using AES-256-GCM via Apple's CryptoKit
        framework. The encryption key is:
      </p>
      <ul>
        <li>Generated randomly on your device (256-bit)</li>
        <li>Stored in the iOS Keychain, protected by the Secure Enclave</li>
        <li>
          If iCloud Sync is enabled, the key is synced via iCloud Keychain
          (end-to-end encrypted by Apple)
        </li>
      </ul>
      <p>
        The encryption key never leaves Apple's secure infrastructure. Doccy's
        developers have no access to your encryption key or your data.
      </p>

      <h2>iCloud Sync</h2>
      <p>
        iCloud Sync is optional and disabled by default. When enabled:
      </p>
      <ul>
        <li>Data is encrypted on your device before upload</li>
        <li>Encrypted blobs are stored in your private CloudKit database</li>
        <li>Only your Apple ID can access the data</li>
        <li>Apple cannot decrypt the data (they do not have your encryption key)</li>
        <li>Synced data is subject to Apple's iCloud terms and privacy policy</li>
      </ul>

      <h2>Subscriptions</h2>
      <p>
        Doccy Pro purchases are managed entirely by Apple and App Store.
        Doccy does not process payments or have access to your payment
        information.
      </p>

      <h2>Notifications</h2>
      <p>
        If you enable expiry reminders (a Pro feature), Doccy schedules local
        notifications on your device. These notifications are managed entirely
        by iOS and do not involve any external server.
      </p>

      <h2>Third-party services</h2>
      <p>
        Doccy does not use any third-party SDKs, analytics services, advertising
        networks, or external servers. The app has zero third-party dependencies.
      </p>

      <h2>Data retention</h2>
      <p>
        Your data exists only on your device (and in your iCloud account if you
        enable sync). If you delete the app, all local data is deleted.
      </p>

      <h2>Changes to this policy</h2>
      <p>
        If this privacy policy changes, the updated version will be published at
        this URL with a new "Last updated" date.
      </p>

      <h2>Contact</h2>
      <p>
        If you have questions about this privacy policy, contact:{' '}
        <a href="mailto:doccy@prutkowski.dev">doccy@prutkowski.dev</a>
      </p>
    </div>
  );
}

export default Privacy;
